<template>
  <Vuedraggable
    v-if="!!props.modelValue"
    :list="props.modelValue"
    class="tour"
    draggable=".tour-day-container:not(.not-draggable)"
    item-key="id"
    v-bind="dragOptions"
    @change="change"
  >
    <template #item="{ element, index }">
      <div
        :key="index"
        :class="{
          'not-draggable ghost-item': props.updatingTour,
          'not-draggable blocked-item': element.isBlocked,
        }"
        class="tour-day-container"
      >
        <div key="tour-day">
          <div v-if="index !== 0" class="w-100 d-flex justify-content-center add-day-wrapper">
            <v-button
              :disabled="props.updatingTour"
              class="btn-add-plus rounded-circle shadow-sm mb-4 btn-add-day-hover"
              size="xs"
              variant="primary"
              @click="addDay(index)"
              title="Add Day"
              style="transition: all 0.2s;"
            >
              <v-icon icon="plus" style="font-size: 10px; line-height: 1;" />
            </v-button>
          </div>

          <div :class="{ 'mt-12': index === 0 }" class="tour-day d-flex flex-row row gx-0 border rounded-xs mb-12 bg-white shadow-sm day-container-v2">
            <!-- Day Info Column (Super Compact) -->
            <div class="border-end bg-light bg-opacity-10 d-flex flex-column align-items-center justify-content-center py-8 px-4" style="width: 130px; flex: none;">
              <div class="date text-xs mb-8 text-dark fw-bold text-center lh-1 text-nowrap px-16" style="font-size: 10px;">
                {{ getDateFormatted(index) }}
              </div>

              <div
                class="day-circle d-flex align-items-center justify-content-center fw-bold mb-8 shadow-sm"
                style="width: 40px; height: 40px; font-size: 18px; border-radius: 50%; border: 2px solid #000000 !important; color: #000000 !important; background: transparent; flex: none;"
              >
                {{ index + 1 }}
              </div>
              
              <v-checkbox
                :id="`template-day-${element.id}-stage`"
                :is-disabled="isTourDayUpdating"
                :label="$t('template.stage')"
                :left="false"
                :modelValue="element.isStage"
                :right="true"
                class="mb-0 fw-bold justify-content-center checkbox-compact"
                for-key="isAllDayActivated"
                @update:modelValue="updateTourDayType($event, element)"
                style="font-size: 11px; --uq-form-check-input-width: 1em;"
              />

              <div v-if="element.isStage" class="mt-12 text-center text-dark fw-bold" style="font-size: 10px;">
                Distance: {{ element.stageLength }} km
              </div>
            </div>

            <!-- Content Column Reverted to Original Structure Layout (Vertical Stack) -->
            <div class="col">
              <div class="row gx-0 h-100">
                <!-- Inputs Section (Vertical stack - Compact gap) -->
                <div class="col-4 border-end bg-light bg-opacity-10 p-4">
                  <template v-if="element.isStage">
                    <v-input-address
                      :id="`template-day-origin-search-${element.id}`"
                      :disabled-input="isTourDayUpdating"
                      :get-address="true"
                      :get-radius-bounds="true"
                      :label="$t('general.shared.origin')"
                      label-span-class="bg-white"
                      :radius-bounds="20000"
                      :value="getDayLocationName(element, 'origin')"
                      class="col-12 mb-0 form-control-compact-v2"
                      input-class="py-1"
                      @address="createLocation(element, $event, true, index)"
                      @input="updateTourDayOrigin($event, element, index)"
                    />

                    <v-input-address
                      :id="`template-day-destination-search-${element.id}`"
                      :disabled-input="isTourDayUpdating"
                      :get-address="true"
                      :get-radius-bounds="true"
                      :label="$t('general.shared.destination')"
                      label-span-class="bg-white"
                      :radius-bounds="20000"
                      :value="getDayLocationName(element, 'destination')"
                      class="col-12 mb-0 form-control-compact-v2"
                      input-class="py-1"
                      @address="createLocation(element, $event, false, index)"
                      @input="updateTourDayDestination($event, element)"
                    />
                  </template>

                  <template v-else>
                    <v-input-address
                      :id="`template-day-location-search-${element.id}`"
                      :disabled-input="isTourDayUpdating"
                      :get-address="true"
                      :get-radius-bounds="true"
                      :label="$t('general.shared.location')"
                      label-span-class="bg-white"
                      :radius-bounds="20000"
                      :value="getDayLocationName(element, 'origin')"
                      class="col-12 mb-0 form-control-compact-v2"
                      @address="createLocation(element, $event, true, index)"
                      @input="updateTourDayOrigin($event, element, index)"
                    />
                  </template>
                </div>

                <!-- Items and Day Actions Section -->
                <div class="col-8 p-4 overflow-visible">
                  <div class="d-flex justify-content-between align-items-start h-100 ps-4">
                    <the-template-day-item-draggable-v2
                      :agency="agency"
                      :can-add-item="
                        (!element.isStage && !!element.originId) ||
                        (!!element.isStage && !!element.originId && !!element.destinationId)
                      "
                      :list="element.items"
                      :template-day-id="element.id"
                      :template-id="templateId"
                      :tour-mapped="props.tourMapped"
                      :updating-tour="props.updatingTour"
                      :origin-id="element.originId"
                      :destination-id="element.destinationId"
                      :is-stage="!!element.isStage"
                      type="tour-day-item"
                      class="flex-grow-1"
                      @emit-reload-tour="emit('reloadTour')"
                      @open-item-details-off-canvas="emit('openItemDetailsOffCanvas', $event)"
                      @move-item-to-another-day="emit('moveItemToAnotherDay', $event)"
                    />

                    <div class="d-flex flex-column border-start ps-8 py-2 ms-8">
                      <v-button
                        :disabled="props.updatingTour"
                        class="btn-day p-0 rounded-circle shadow-sm mb-4 d-flex align-items-center justify-content-center bg-light border"
                        size="xs"
                        variant="icon"
                        title="Refresh Day"
                        @click="emit('reloadTour')"
                        style="width: 24px; height: 24px;"
                      >
                        <v-icon icon="refresh" size="xs" class="text-primary" />
                      </v-button>

                      <v-button
                        :disabled="props.updatingTour"
                        class="btn-day p-0 bg-light rounded-circle shadow-sm d-flex align-items-center justify-content-center border"
                        size="xs"
                        variant="icon"
                        title="Delete Day"
                        @click="removeDay(element, index)"
                        style="width: 24px; height: 24px;"
                      >
                        <v-icon icon="trash" size="xs" class="text-danger" />
                      </v-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Vuedraggable>
</template>

<script lang="ts" setup>
import { computed, onMounted, getCurrentInstance, PropType, ref } from "vue";
import Vuedraggable from "vuedraggable";
import { VCheckbox } from "@uniqoders/form";
import { TYPE, useToast } from "vue-toastification";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";
import VInputAddress from "@/components/vendor/basic/form/VInputAddress.vue";
import Location from "@/api/objects/Location";
import TheTemplateDayItemDraggableV2 from "@/components/template/V2/TheTemplateDayItemDraggableV2.vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits([
  "dayMoved",
  "emitAddDay",
  "reloadTour",
  "openItemDetailsOffCanvas",
  "moveItemToAnotherDay",
  "openDayInformationOffCanvas",
]);

const props = defineProps({
  modelValue: {
    type: Array as PropType<Record<string, unknown>[]>,
    required: false,
  },
  type: {
    type: String,
    required: false,
    default: "",
  },
  templateId: {
    type: Number,
    required: true,
  },
  agency: {
    type: String,
    required: true,
  },
  updatingTour: {
    type: Boolean,
    required: false,
    default: false,
  },
  initDate: {
    type: String,
    required: true,
    default: "",
  },
  endDate: {
    type: String,
    required: true,
    default: "",
  },
  currentTourDay: {
    type: [String, Number],
    required: false,
    default: "",
  },
  tourMapped: {
    type: Object,
    required: true,
  },
});

const isTourDayUpdating = ref(false);
const locations = ref<Location[]>([]);
const { t } = useI18n();
const toast = useToast();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const $toast = instance?.appContext.config.globalProperties.$toast;

const dragOptions = computed(() => ({
  animation: 0,
  group: "tour-day",
  disabled: false,
  ghostClass: "ghost",
}));

const loadLocations = async () => {
  locations.value = await api.location.all();
};

const searchLocation = (locationId: number | string | null | undefined): string => {
  if (locationId === null || locationId === undefined || locationId === "") {
    return "";
  }

  const location = locations.value.find((loc) => Number(loc.id) === Number(locationId));
  return location ? location.name : "";
};

const getDayLocationName = (day: Record<string, any>, field: "origin" | "destination"): string => {
  const relation = day[field];
  if (relation?.name) {
    return relation.name;
  }

  const locationId = field === "origin"
    ? (day.originId ?? day.origin_id)
    : (day.destinationId ?? day.destination_id);

  return searchLocation(locationId);
};

const cacheLocation = (location: Location) => {
  if (!locations.value.some((loc) => Number(loc.id) === Number(location.id))) {
    locations.value.push(location);
  }
};

const getDateFormatted = (index: number): string => {
  if (!props.initDate) return "";
  const start = DateTime.fromISO(props.initDate);
  const date = start.plus({ days: index });
  return date.toFormat("ccc, dd MMM yyyy");
};

const change = async () => {
  // After drag, update the order of all days and persist to backend
  if (props.modelValue && Array.isArray(props.modelValue)) {
    const updates = props.modelValue.map(async (day: any, idx: number) => {
      if (day.order !== idx + 1) {
        day.order = idx + 1;
        await api.activityService.updateTourDay(props.agency, props.templateId, day.id, {
          is_stage: day.isStage,
          order: day.order,
          origin: day.originId ?? day.origin_id,
          destination: day.destinationId ?? day.destination_id,
        });
      }
    });
    await Promise.all(updates);
    emit("reloadTour");
  }
};

const addDay = (order: number) => {
  emit("emitAddDay", order);
};

const removeAllItems = async (tourDay: Record<string, any>) => {
  tourDay.items.forEach((item: Record<string, any>) => {
    api.activityService.deleteActivityDayItem(props.agency, props.templateId, tourDay.id, item.id);
  });
};

const removeDay = async (tourDay: Record<string, any>, dayNumber: number) => {
  await $modal.delete({
    title: t("template.deleteDayTitle"),
    text: t("template.deleteDayText", { dayNumber: dayNumber + 1 }),
    deleteButtonText: t("general.button.delete"),
    rightButtonClasses: "btn-tertiary",
    deleteButtonCallback: () => doRemoveDay(tourDay),
  });
};

const doRemoveDay = async (tourDay: Record<string, any>) => {
  const loadingToast = $toast.loading(t("general.button.deleting"), "toast-remove-tour-day");

  try {
    await removeAllItems(tourDay);

    await api.activityService.deleteTourDay(props.agency, props.templateId, tourDay.id);

    emit("reloadTour");

    loadingToast.update("toast-remove-tour-day", {
      content: t("template.dayDeleted"),
      options: {
        type: TYPE.SUCCESS,
        icon: "icon icon-check-circle-fill",
        timeout: 5000,
      },
    });
  } catch (e: any) {
    loadingToast.update("toast-remove-tour-day", {
      content: t("template.dayDeleteError"),
      options: {
        type: TYPE.ERROR,
        icon: "icon icon-exclamation-triangle-fill",
        timeout: 5000,
      },
    });
    console.error(e.response.data);
    toast.error(e.response.data.message);
  }
};

const createLocation = async (
  tourDay: Record<string, any>,
  address: Record<string, any>,
  isOrigin = true,
  position = null
) => {
  const location = await api.location.create({
    name: address.name ? address.name : "-",
    provider_id: address.placeId,
    lat: address.lat,
    lng: address.lng,
    country: address.country.longName,
    province: address.regionProvince.longName,
    state: address.stateCommunity.longName,
  });

  cacheLocation(location);

  if (isOrigin) {
    tourDay.originId = location.id;
    tourDay.origin_id = location.id;
    tourDay.origin = location;
  } else {
    tourDay.destinationId = location.id;
    tourDay.destination_id = location.id;
    tourDay.destination = location;
  }

  let positionCode = undefined;
  if (position !== null && props.tourMapped && position + 1 === props.tourMapped.tourDays.length) {
    positionCode = 1;
  } else if (position !== null && position === 0) {
    positionCode = position;
  }

  await updateTourDay(tourDay, positionCode);
};

const updateTourDayType = async (checkboxValue: boolean, tourDay: Record<string, any>) => {
  if (!isTourDayUpdating.value) {
    tourDay.type = checkboxValue ? "stage" : "rest";
    tourDay.isStage = checkboxValue;
    tourDay.is_stage = checkboxValue;
    await updateTourDay(tourDay);
  }
};

const updateTourDayOrigin = async (value: string, tourDay: Record<string, any>, position = 0) => {
  if (!value) {
    tourDay.originId = null;
    tourDay.origin_id = null;
    tourDay.origin = null;
    await updateTourDay(tourDay, position);
  }
};

const updateTourDayDestination = async (value: string, tourDay: Record<string, any>) => {
  if (!value) {
    tourDay.destinationId = null;
    tourDay.destination_id = null;
    tourDay.destination = null;
    await updateTourDay(tourDay);
  }
};

const updateTourDay = async (tourDay: Record<string, any>, position: any = null) => {
  if (!isTourDayUpdating.value) {
    try {
      isTourDayUpdating.value = true;

      await api.activityService.updateTourDay(props.agency, props.templateId, tourDay.id, {
        is_stage: !!tourDay.isStage,
        order: tourDay.order,
        origin: tourDay.originId ?? tourDay.origin_id,
        destination: tourDay.destinationId ?? tourDay.destination_id,
      });

      emit("reloadTour");

      isTourDayUpdating.value = false;
    } catch (e: any) {
      isTourDayUpdating.value = false;
      console.error(e);
      toast.error(e.response?.data?.message || "An error occurred");
    }
  }
};

onMounted(async () => {
  await loadLocations();
});
</script>

<style lang="scss">
.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}

.list-group {
  min-height: 20px;
}

.list-enter-from {
  opacity: 0;
  transform: scaleY(0.25);
}

.list-enter-active,
.list-leave-active {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-to {
  opacity: 0;
  transform: scaleY(0);
}

.list-move {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-active {
  position: relative;
}

.list-group-item {
  display: block !important;
  padding: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  width: 100% !important;
}

.ls-1 {
  letter-spacing: 1px;
}

.tour-day-container {
  position: static !important; /* Allow child z-index to break out if needed */
  z-index: auto !important;
}

.tour-day {
  position: relative;
  z-index: 1;
  overflow: visible;
  transition: all 0.3s ease;
  &:hover,
  &:has(.item-selected) {
    border-color: var(--uq-primary) !important;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05) !important;
    z-index: 10; /* Slightly elevate on hover */
  }
}

.day-circle {
  border: 2px solid #fff;
}

.checkbox-compact :deep(.v-form-label) {
  margin-bottom: 0 !important;
  line-height: 1;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.9375rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.8125rem !important;
}
.btn-add-plus {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  min-height: 20px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: 0 !important;
}

.add-day-wrapper {
  height: 24px;
}

.btn-add-day-hover {
  opacity: 0;
  visibility: hidden;
}

.add-day-wrapper:hover .btn-add-day-hover {
  opacity: 1;
  visibility: visible;
}

.day-container-v2 {
  border-color: #F4F4F4 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  overflow: visible !important;
}

.form-control-compact-v2 :deep(.form-control) {
  padding-top: 1px !important;
  padding-bottom: 1px !important;
  min-height: 24px !important;
  font-size: 0.75rem !important;
}

.form-control-compact-v2 :deep(.label) {
  font-size: 0.7rem !important;
  font-weight: 700;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 0;
  transform: translate(0, 45%);
}

.form-control-compact-v2 :deep(.label span) {
  background-color: #f8f9fa !important;
}
</style>
